import { textColorFields } from '../_shared/textColor.js';
import { universalFields } from '../_shared/universal.js';
import { headingHighlightFields } from '../_shared/headingHighlight.js';
/**
 * collection-list — a grid (or slider) of COLLECTION cards (cover image + name), each linking to its
 * collection page, at Shrine `collection-list` / `card-collection` parity. FROM-SCRATCH rebuild.
 *
 * SOURCE model (the founder's ask — "show my real collections"):
 *   • `all`      → automatically show the STORE'S real collections (host-filled `resolved.storeCollections`),
 *                  capped by `maxCollections`. Zero manual picking. This is the DEFAULT.
 *   • `selected` → hand-picked `collection` BLOCKS (curation / ordering), resolved by id.
 * When nothing resolves (builder preview / offline) the section falls back to demo collections so a fresh
 * grid always renders.
 *
 * The header (heading word-highlight + pattern background), the grid↔slider layout, and the card (ratio,
 * corner radius, title under/over-image + scrim, alignment, line clamp) all reuse the shared engines. The
 * universal "Section" group controls scheme / padding / width.
 *
 * REDESIGN 2026-09-21 (« ça n'a pas l'air professionnel »): eyebrow + description + split header, a
 * "featured first" layout, 5 columns, card gaps, centring when a row is not full, three more image
 * ratios, card style / border / shadow / hover, gradient overlays, title weight / capitals / tracking, the
 * collection description and a per-card "Discover" link. Every new field defaults to what keeps an
 * existing store readable; the only visible changes on old sections are the centred short row, the
 * dropped 1px image border and the image zoom on hover.
 */
export const collectionListSchema = {
    type: 'collection-list',
    name: 'Collection list',
    version: 2,
    templates: ['index', 'collection', 'collections', 'page', 'thank-you'],
    fields: [
        // ── Layout ───────────────────────────────────────────────────────────────
        {
            // Ajouté 2026-08-20 : sans lui cette section n'a PAS d'ancre HTML, donc une entrée d'`anchor-nav`
            // qui la vise ne défile nulle part — un lien bien formé et parfaitement mort, qu'aucun test « pas
            // de href nu » ne voit. Seules quatre sections de la bibliothèque le déclaraient ; la barre de
            // sommaire de Roast a dû être amputée de moitié pour cette seule raison.
            id: 'displaySectionId',
            type: 'checkbox',
            label: 'Display section ID',
            group: 'General',
            info: 'Adds an anchor id to this section so a link can scroll to it.',
            default: false,
        },
        // ── Content ─────────────────────────────────────────────────────────────
        {
            id: 'heading',
            type: 'text',
            label: 'Heading',
            group: 'Content',
            default: 'Collections',
            maxLength: 60,
        },
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: 'Content',
            default: 'h2',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'headingSizeDesktop',
            type: 'range',
            label: 'Custom heading size',
            group: 'Content',
            min: 20,
            max: 64,
            step: 2,
            unit: 'px',
            default: 30,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'headingAlignment',
            type: 'select',
            label: 'Heading alignment',
            group: 'Content',
            default: 'center',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        // Redesign 2026-09-21 — the band read as a bare title over a lone card. A small EYEBROW above the
        // heading, a short paragraph under it and a "heading + View all on one row" header are the three
        // header moves every polished catalogue page makes; all three are empty/off by default.
        {
            id: 'subheading',
            type: 'text',
            label: 'Subheading',
            group: 'Content',
            info: 'A short line shown above the heading, in small capitals.',
            default: '',
            maxLength: 60,
        },
        {
            id: 'description',
            type: 'textarea',
            label: 'Description',
            group: 'Content',
            default: '',
            rows: 3,
            maxLength: 300,
        },
        {
            id: 'headerLayout',
            type: 'select',
            label: 'Header layout',
            group: 'Content',
            info: '“Side by side” puts the “View all” link on the same row as the heading.',
            default: 'stacked',
            options: [
                { value: 'stacked', label: 'Stacked' },
                { value: 'split', label: 'Side by side' },
            ],
        },
        // ── Collections (SOURCE) ────────────────────────────────────────────────
        {
            id: 'source',
            type: 'select',
            label: 'Collections shown',
            group: 'Collections',
            default: 'all',
            // “below” was a lie: the `collection` blocks are added from the SECTION LIST rail, not from
            // this panel — a seller who switched to “Selected collections” was left staring at a form with
            // nothing to pick in it. Name the block instead of a direction (which also flips under RTL).
            info: 'Show all of your store’s collections automatically, or tick the ones you want below.',
            options: [
                { value: 'all', label: 'All my collections' },
                { value: 'selected', label: 'Selected collections' },
            ],
        },
        // The checklist behind "Selected collections" (22/09/2026): ticks and unticks this section's
        // `collection` blocks. Blocks stay the stored truth (the storefront resolves them), the seller just
        // no longer has to find the block rail to fill the page.
        {
            id: 'pickedCollections',
            type: 'collection-blocks',
            blockType: 'collection',
            label: 'Choose the collections',
            group: 'Collections',
            info: 'Tick the collections to show on the page, in this order.',
            visibleWhen: { field: 'source', equals: 'selected' },
        },
        {
            id: 'maxCollections',
            type: 'range',
            label: 'Maximum to show',
            group: 'Collections',
            min: 2,
            max: 24,
            step: 1,
            default: 6,
            visibleWhen: { field: 'source', equals: 'all' },
        },
        // ── Heading highlight (shared engine) — style words wrapped in ** ** (1) / * * (2). ─────────────
        ...headingHighlightFields(1),
        ...headingHighlightFields(2),
        // ── Background pattern (shared engine — 13 patterns). ────────────────────────────────────────────
        {
            id: 'patternBg',
            type: 'select',
            label: 'Background pattern',
            group: 'Background pattern',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'dotted', label: 'Dots' },
                { value: 'grid', label: 'Grid' },
                { value: 'curved-grid', label: 'Curved grid' },
                { value: 'diagonal-stripes', label: 'Stripes' },
                { value: 'zigzag', label: 'ZigZag' },
                { value: 'arrow-pattern', label: '3D Boxes' },
                { value: 'radial-rings', label: 'Rings' },
                { value: 'diamond-grid-1', label: 'Diamond grid 1' },
                { value: 'diamond-grid-2', label: 'Diamond grid 2' },
                { value: 'quatrefoils', label: 'Quatrefoils' },
                { value: 'ovals', label: 'Ovals' },
                { value: 'hexagons', label: 'Hexagons' },
                { value: 'curvy', label: 'Curvy' },
            ],
        },
        {
            id: 'patternColor',
            type: 'color',
            label: 'Pattern color',
            group: 'Background pattern',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternSize',
            type: 'range',
            label: 'Pattern size / spacing',
            group: 'Background pattern',
            min: 4,
            max: 80,
            step: 2,
            unit: 'px',
            default: 20,
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternThickness',
            type: 'range',
            label: 'Line / dot thickness',
            group: 'Background pattern',
            min: 1,
            max: 10,
            step: 1,
            unit: 'px',
            default: 2,
            visibleWhen: {
                field: 'patternBg',
                equals: ['dotted', 'grid', 'diagonal-stripes', 'radial-rings'],
            },
        },
        {
            id: 'patternAngle',
            type: 'range',
            label: 'Stripe angle',
            group: 'Background pattern',
            min: 0,
            max: 180,
            step: 15,
            unit: 'deg',
            default: 45,
            visibleWhen: { field: 'patternBg', equals: 'diagonal-stripes' },
        },
        {
            id: 'patternOpacityMode',
            type: 'select',
            label: 'Opacity type',
            group: 'Background pattern',
            default: 'low',
            options: [
                { value: 'low', label: 'Low' },
                { value: 'normal', label: 'Normal' },
            ],
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternOpacityLow',
            type: 'range',
            label: 'Opacity',
            group: 'Background pattern',
            min: 1,
            max: 20,
            step: 1,
            unit: '%',
            default: 7,
            visibleWhen: { field: 'patternOpacityMode', equals: 'low' },
        },
        {
            id: 'patternOpacityNormal',
            type: 'range',
            label: 'Opacity',
            group: 'Background pattern',
            min: 20,
            max: 100,
            step: 5,
            unit: '%',
            default: 80,
            visibleWhen: { field: 'patternOpacityMode', equals: 'normal' },
        },
        // ── Layout ──────────────────────────────────────────────────────────────
        {
            id: 'layout',
            type: 'select',
            label: 'Layout',
            group: 'Layout',
            info: '“Featured first” makes the first collection twice as large (needs 3 or more columns and no slider).',
            default: 'grid',
            options: [
                { value: 'grid', label: 'Grid' },
                { value: 'featured', label: 'Featured first' },
            ],
        },
        {
            id: 'columns',
            type: 'select',
            label: 'Columns',
            group: 'Layout',
            default: '3',
            options: [
                { value: '2', label: '2 columns' },
                { value: '3', label: '3 columns' },
                { value: '4', label: '4 columns' },
                { value: '5', label: '5 columns' },
            ],
        },
        {
            id: 'columnsMobile',
            type: 'select',
            label: 'Columns on mobile',
            group: 'Layout',
            default: '1',
            options: [
                { value: '1', label: '1 column' },
                { value: '2', label: '2 columns' },
            ],
        },
        {
            id: 'cardGap',
            type: 'range',
            label: 'Space between cards',
            group: 'Layout',
            min: 0,
            max: 48,
            step: 2,
            unit: 'px',
            default: 20,
        },
        {
            id: 'cardGapMobile',
            type: 'range',
            label: 'Space between cards on mobile',
            group: 'Layout',
            min: 0,
            max: 32,
            step: 2,
            unit: 'px',
            default: 12,
        },
        {
            // The screenshot that started the redesign: ONE collection in a 3-column grid, pinned to the left
            // column with two-thirds of the band empty. Fewer cards than columns is the common case for a young
            // store, so the cards are centred by default — at the width a full row would give them.
            // Renamed to a plain alignment (22/09/2026) and given the third side: « aligner les collections
            // soit left, centre ou right ». Same stored values as before (`start` / `center`), plus `end`.
            id: 'alignIncomplete',
            type: 'select',
            label: 'Collections alignment',
            group: 'Layout',
            default: 'center',
            info: 'Where the cards sit when a row is not full.',
            options: [
                { value: 'start', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'end', label: 'Right' },
            ],
        },
        {
            id: 'imageRatio',
            type: 'select',
            label: 'Image ratio',
            group: 'Layout',
            default: 'square',
            options: [
                { value: 'square', label: 'Square (1:1)' },
                { value: 'portrait', label: 'Portrait (4:5)' },
                { value: 'tall', label: 'Tall (2:3)' },
                { value: 'landscape', label: 'Landscape (4:3)' },
                { value: 'wide', label: 'Wide (16:9)' },
                { value: 'circle', label: 'Circle' },
            ],
        },
        {
            id: 'showViewAll',
            type: 'checkbox',
            label: 'Show “View all” button',
            group: 'Content',
            default: false,
        },
        {
            id: 'viewAllLabel',
            type: 'text',
            label: '“View all” label',
            group: 'Content',
            default: 'View all',
            defaultKey: 'collection.view_all',
            maxLength: 40,
            visibleWhen: { field: 'showViewAll', equals: true },
        },
        {
            id: 'viewAllStyle',
            type: 'select',
            label: '“View all” style',
            group: 'Content',
            default: 'button',
            options: [
                { value: 'button', label: 'Button' },
                { value: 'link', label: 'Text link with arrow' },
            ],
            visibleWhen: { field: 'showViewAll', equals: true },
        },
        // ── Slider (turn the grid into a carousel when there's overflow). ───────────────────────────────
        {
            id: 'enableSlider',
            type: 'checkbox',
            label: 'Enable desktop slider',
            group: 'Slider',
            default: false,
        },
        {
            id: 'sliderArrows',
            type: 'select',
            label: 'Arrows',
            group: 'Slider',
            default: 'sides',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
                { value: 'sides', label: 'On the sides' },
            ],
            visibleWhen: { field: 'enableSlider', equals: true },
        },
        {
            id: 'sliderDots',
            type: 'select',
            label: 'Dots',
            group: 'Slider',
            default: 'under',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
            ],
            visibleWhen: { field: 'enableSlider', equals: true },
        },
        // ── Collection card (Shrine card-collection parity + the 2026-09-21 redesign). ──────────────────
        {
            id: 'cardStyle',
            type: 'select',
            label: 'Card style',
            group: 'Collection card',
            info: 'Plain shows the image and the text on the section background. Surface wraps them in a filled card.',
            default: 'plain',
            options: [
                { value: 'plain', label: 'Plain' },
                { value: 'surface', label: 'Filled card' },
            ],
        },
        {
            id: 'cardBorder',
            type: 'checkbox',
            label: 'Show border',
            group: 'Collection card',
            default: false,
        },
        {
            id: 'cardShadow',
            type: 'select',
            label: 'Shadow',
            group: 'Collection card',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'soft', label: 'Soft' },
                { value: 'strong', label: 'Strong' },
            ],
        },
        {
            id: 'hoverEffect',
            type: 'select',
            label: 'Hover effect',
            group: 'Collection card',
            info: 'Ignored by readers who asked their system for reduced motion.',
            default: 'zoom',
            options: [
                { value: 'none', label: 'None' },
                { value: 'zoom', label: 'Zoom the image' },
                { value: 'lift', label: 'Lift the card' },
                { value: 'zoom-lift', label: 'Zoom and lift' },
            ],
        },
        {
            id: 'cardTitlePosition',
            type: 'select',
            label: 'Title position',
            group: 'Collection card',
            default: 'under',
            options: [
                { value: 'under', label: 'Under the image' },
                { value: 'over-top', label: 'Over the image — top' },
                { value: 'over-center', label: 'Over the image — center' },
                { value: 'over-bottom', label: 'Over the image — bottom' },
            ],
        },
        {
            id: 'cardTextAlignment',
            type: 'select',
            label: 'Title alignment',
            group: 'Collection card',
            default: 'left',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        {
            id: 'cardTitleSize',
            type: 'range',
            label: 'Title size',
            group: 'Collection card',
            min: 14,
            max: 36,
            step: 1,
            unit: 'px',
            default: 18,
        },
        {
            id: 'cardCornerRadius',
            type: 'range',
            label: 'Image corner radius',
            group: 'Collection card',
            min: 0,
            max: 40,
            step: 2,
            unit: 'px',
            default: 0,
            visibleWhen: { field: 'imageRatio', notEquals: 'circle' },
        },
        {
            id: 'cardTitleWeight',
            type: 'select',
            label: 'Title weight',
            group: 'Collection card',
            default: 'semibold',
            options: [
                { value: 'regular', label: 'Regular' },
                { value: 'medium', label: 'Medium' },
                { value: 'semibold', label: 'Semibold' },
                { value: 'bold', label: 'Bold' },
            ],
        },
        {
            id: 'cardTitleUppercase',
            type: 'checkbox',
            label: 'Title in capitals',
            group: 'Collection card',
            default: false,
        },
        {
            id: 'cardTitleLetterSpacing',
            type: 'range',
            label: 'Title letter spacing',
            group: 'Collection card',
            min: 0,
            max: 6,
            step: 1,
            unit: 'px',
            default: 0,
        },
        {
            id: 'cardTitleLines',
            type: 'select',
            label: 'Limit title to',
            group: 'Collection card',
            default: 'unlimited',
            options: [
                { value: 'unlimited', label: 'Don’t limit' },
                { value: '1', label: '1 line' },
                { value: '2', label: '2 lines' },
            ],
        },
        {
            id: 'showProductCount',
            type: 'checkbox',
            label: 'Show product count',
            group: 'Collection card',
            default: false,
        },
        {
            id: 'showDescription',
            type: 'checkbox',
            label: 'Show the collection description',
            group: 'Collection card',
            default: false,
        },
        {
            id: 'descriptionLines',
            type: 'select',
            label: 'Limit description to',
            group: 'Collection card',
            default: '2',
            options: [
                { value: '1', label: '1 line' },
                { value: '2', label: '2 lines' },
                { value: '3', label: '3 lines' },
            ],
            visibleWhen: { field: 'showDescription', equals: true },
        },
        {
            id: 'showCardCta',
            type: 'checkbox',
            label: 'Show a “Discover” link on each card',
            group: 'Collection card',
            default: false,
        },
        {
            id: 'cardCtaLabel',
            type: 'text',
            label: 'Card link label',
            group: 'Collection card',
            default: 'Discover',
            defaultKey: 'hero.shop_now',
            maxLength: 40,
            visibleWhen: { field: 'showCardCta', equals: true },
        },
        {
            id: 'cardCtaStyle',
            type: 'select',
            label: 'Card link style',
            group: 'Collection card',
            default: 'arrow',
            options: [
                { value: 'arrow', label: 'Text with arrow' },
                { value: 'underline', label: 'Underlined text' },
                { value: 'button', label: 'Outlined button' },
            ],
            visibleWhen: { field: 'showCardCta', equals: true },
        },
        {
            id: 'overlayStyle',
            type: 'select',
            label: 'Overlay style (over image)',
            group: 'Collection card',
            info: 'A gradient fades the overlay colour from the title’s edge into the photo; a band tints only the title area.',
            default: 'gradient',
            options: [
                { value: 'gradient', label: 'Gradient over the photo' },
                { value: 'band', label: 'Tinted band behind the title' },
                { value: 'none', label: 'No overlay' },
            ],
            visibleWhen: { field: 'cardTitlePosition', notEquals: 'under' },
        },
        {
            id: 'cardTitleOverlayColor',
            type: 'color',
            label: 'Title overlay color (over image)',
            group: 'Collection card',
            default: { ref: 'custom', value: '#000000' },
            visibleWhen: { field: 'overlayStyle', notEquals: 'none' },
        },
        {
            id: 'cardTitleOverlayOpacity',
            type: 'range',
            label: 'Title overlay opacity',
            group: 'Collection card',
            min: 0,
            max: 100,
            step: 5,
            unit: '%',
            default: 40,
            visibleWhen: { field: 'overlayStyle', notEquals: 'none' },
        },
        {
            id: 'cardTitleOverColor',
            type: 'color',
            label: 'Title text color (over image)',
            group: 'Collection card',
            default: { ref: 'custom', value: '#ffffff' },
            visibleWhen: { field: 'cardTitlePosition', notEquals: 'under' },
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // ── Section (universal) ──────────────────────────────────────────────────
        ...universalFields,
    ],
    blocks: [
        {
            type: 'collection',
            name: 'Collection',
            max: 24,
            fields: [{ id: 'ref', type: 'collection-picker', label: 'Collection' }],
        },
    ],
    maxBlocks: 24,
    migrate(fromVersion, settings) {
        // v1 → v2: v1 had no `source` (it always used picked blocks, else demo). Preserve intent — a v1
        // section keeps showing exactly what it did by pinning `source: 'selected'`; new sections default to
        // 'all'. v1's `imageRatio: 'landscape'` (dropped in v2) collapses to 'square'.
        if (fromVersion >= 2)
            return settings;
        const next = { ...settings };
        if (next.source === undefined)
            next.source = 'selected';
        if (next.imageRatio === 'landscape')
            next.imageRatio = 'square';
        return next;
    },
};
export const collectionListPresets = [
    {
        name: 'Collection list',
        settings: {
            heading: '@t:section.collections',
            headingAlignment: 'center',
            source: 'all',
            maxCollections: 6,
            columns: '3',
            imageRatio: 'square',
            cardTitlePosition: 'under',
        },
        // No picked collections + source 'all' → renders the store's real collections (or demo in the builder).
        blocks: [],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const collectionListMeta = {
    label: 'Collection list',
    group: 'Home',
    icon: 'package',
};
//# sourceMappingURL=schema.js.map